import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { MsalProvider } from '@azure/msal-react';
import { EventType } from '@azure/msal-browser';
import theme from './theme';
import { msalInstance, AUTH_ENABLED } from './authConfig';
import { registerAuthInterceptor, registerErrorInterceptor, registerViewAsInterceptor } from './authedAxios';
import AuthGate from './AuthGate';
import { MeProvider } from './MeContext';
import { ViewAsProvider } from './ViewAsContext';
import PhoneFind from './pages/PhoneFind';
import KitList from './pages/KitList';
import SignContract from './pages/SignContract';
import SubmitFeedback from './pages/SubmitFeedback';
import './index.css';

const root = ReactDOM.createRoot(document.getElementById('root'));

// `authError` is any failure from initialising MSAL / completing the redirect we
// came back on; the AuthGate shows it on the sign-in page so the user can retry.
function renderApp(authError) {
  registerErrorInterceptor();
  registerAuthInterceptor();
  registerViewAsInterceptor();

  const tree = (
    <BrowserRouter>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthGate initialError={authError}>
          <ViewAsProvider>
            <MeProvider>
              <App />
            </MeProvider>
          </ViewAsProvider>
        </AuthGate>
      </ThemeProvider>
    </BrowserRouter>
  );

  // Only wrap in MsalProvider when auth is on; otherwise MSAL is never used.
  root.render(
    AUTH_ENABLED ? <MsalProvider instance={msalInstance}>{tree}</MsalProvider> : tree
  );
}

function bootstrapApp() {
  if (AUTH_ENABLED) {
    // MSAL v4 requires initialize() before any other API call.
    msalInstance
      .initialize()
      .then(() => {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          msalInstance.setActiveAccount(accounts[0]);
        }
        // Keep the active account in sync after an interactive login.
        msalInstance.addEventCallback((event) => {
          if (event.eventType === EventType.LOGIN_SUCCESS && event.payload?.account) {
            msalInstance.setActiveAccount(event.payload.account);
          }
        });
        return msalInstance.handleRedirectPromise();
      })
      .then(() => renderApp())
      .catch((err) => {
        // Still render, so the AuthGate can surface the error and offer a retry.
        console.error('MSAL initialization failed', err);
        renderApp(err);
      });
  } else {
    renderApp();
  }
}

// Four pages are public, because the person opening them can't sign in: the Phone
// Finder locator (a casualty follows a texted link), the contents list behind the
// QR code on a container (whoever scanned it is holding the bag), contract
// signing for an event organiser (who has no account; they follow an emailed link),
// and the feedback and complaints form (anyone at all; there is no token, the
// server only ever accepts one new row from it — see routers/feedback_public.py).
// Render them
// directly here — BEFORE (and outside) the auth gate, MSAL and the app providers — so
// they never trigger a sign-in redirect or need a token. In both cases the token in
// the URL is the only credential and the backend validates it. Every other path falls
// through to the normal app.
const PUBLIC_PAGES = [
  ['/phonefind', PhoneFind],
  ['/kit', KitList],
  ['/sign', SignContract],
  ['/submit-feedback', SubmitFeedback],
];

const path = window.location.pathname.replace(/\/+$/, '');
const publicPage = PUBLIC_PAGES.find(([prefix]) => path === prefix || path.startsWith(prefix + '/'));

if (publicPage) {
  const [, Page] = publicPage;
  root.render(
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Page />
    </ThemeProvider>
  );
} else {
  bootstrapApp();
}
